import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// ─── Eagerly loaded (always needed on first paint) ───
import AppHeader from './components/AppHeader';
import MobileBottomNav from './components/MobileBottomNav';
import SupportWidget from './components/SupportWidget';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';

// ─── Lazy loaded pages (downloaded only when visited) ───
const LandingPage               = lazy(() => import('./pages/LandingPage'));
const LoginPage                 = lazy(() => import('./pages/LoginPage'));
const ClientDashboard           = lazy(() => import('./pages/ClientDashboard'));
const CEODashboard              = lazy(() => import('./pages/CEODashboard'));
const SubcontractorDashboard    = lazy(() => import('./pages/SubcontractorDashboard'));
const SalesmanDashboard         = lazy(() => import('./pages/SalesmanDashboard'));
const EstimatorPage             = lazy(() => import('./pages/EstimatorPage'));
const AIAssistantPage           = lazy(() => import('./pages/AIAssistantPage'));
const AdvicePage                = lazy(() => import('./pages/AdvicePage'));
const GuestConsultationPage     = lazy(() => import('./pages/GuestConsultationPage'));
const CompanyPage               = lazy(() => import('./pages/CompanyPage'));
const CompanySettingsPage       = lazy(() => import('./pages/CompanySettingsPage'));
const PaymentSuccess            = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel             = lazy(() => import('./pages/PaymentCancel'));
const ConstructionDashboard     = lazy(() => import('./pages/ConstructionDashboard'));
const ConstructionInfographic   = lazy(() => import('./pages/ConstructionInfographic'));
const BudgetToolsPage           = lazy(() => import('./pages/BudgetToolsPage'));
const SubcontractorPoolPage     = lazy(() => import('./pages/SubcontractorPoolPage'));
const DocumentsPage             = lazy(() => import('./pages/DocumentsPage'));
const ProfilePage               = lazy(() => import('./pages/ProfilePage'));
const FreeEstimatorPage         = lazy(() => import('./pages/FreeEstimatorPage'));
const ChatPage                  = lazy(() => import('./pages/ChatPage'));
const MembershipPage            = lazy(() => import('./pages/MembershipPage'));
const AdminDueDiligence         = lazy(() => import('./pages/AdminDueDiligence'));
const HousePlansMarketplace     = lazy(() => import('./pages/HousePlansMarketplace'));
const ClientGuidePage           = lazy(() => import('./pages/ClientGuidePage'));
const ConstructionProjectsPage  = lazy(() => import('./pages/ConstructionProjectsPage'));
const CRMPage                   = lazy(() => import('./pages/CRMPage'));
const ProfessionalEstimatorPage = lazy(() => import('./pages/ProfessionalEstimatorPage'));
const ProjectHealthDashboard    = lazy(() => import('./pages/ProjectHealthDashboard'));
const AIConstructionAgent       = lazy(() => import('./components/AIConstructionAgent'));
const ServiceProviderRegistration = lazy(() => import('./pages/ServiceProviderRegistration'));
const ClientProjectSubmission   = lazy(() => import('./pages/ClientProjectSubmission'));
const CEOAdminSection           = lazy(() => import('./pages/CEOAdminSection'));
const PricingDatabaseAdmin      = lazy(() => import('./pages/PricingDatabaseAdmin'));
const MarketplaceListings       = lazy(() => import('./pages/MarketplaceListings'));
const CEODigestDashboard        = lazy(() => import('./pages/CEODigestDashboard'));
const CustomDesignRequest       = lazy(() => import('./pages/CustomDesignRequest'));
const PlanPurchaseSuccess       = lazy(() => import('./pages/PlanPurchaseSuccess'));
const PostJob                   = lazy(() => import('./pages/PostJob'));
const MarketplaceJobDetails     = lazy(() => import('./pages/MarketplaceJobDetails'));
const ContractorDashboard       = lazy(() => import('./pages/ContractorDashboard'));
const AboutPage                 = lazy(() => import('./pages/AboutPage'));
const FAQPage                   = lazy(() => import('./pages/FAQPage'));
const AgentsPage                = lazy(() => import('./pages/AgentsPage'));
const LLMDiscoveryPage          = lazy(() => import('./pages/LLMDiscoveryPage'));
const JobsBoardPage             = lazy(() => import('./pages/JobsBoardPage'));
const BookConsultationPage      = lazy(() => import('./pages/BookConsultationPage'));
const ContactSalesPage          = lazy(() => import('./pages/ContactSalesPage'));
const CrownPoolPage             = lazy(() => import('./pages/CrownPoolPage'));
const CrownPoolSuccessPage      = lazy(() => import('./pages/CrownPoolSuccessPage'));
const PrivacyPolicyPage         = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage       = lazy(() => import('./pages/TermsConditionsPage'));
const TeamApprovalsPage         = lazy(() => import('./pages/TeamApprovalsPage'));
const AITrainingDashboard       = lazy(() => import('./pages/AITrainingDashboard'));
const VendorRegistrationPage    = lazy(() => import('./pages/VendorRegistrationPage'));
const ContractTemplatesPage     = lazy(() => import('./pages/ContractTemplatesPage'));
const ContractSigningPage       = lazy(() => import('./pages/ContractSigningPage'));

// ─── Loading fallback ───
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Loading...</p>
      </div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Transfer conversations when user logs in
const transferConversations = async (userId) => {
  try {
    const sessionId = localStorage.getItem('feasibility_session');
    if (!sessionId) return;
    
    const res = await fetch(`${API}/conversations/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, user_id: userId })
    });
    const data = await res.json();
    if (data.conversations_transferred > 0) {
      console.log(`Transferred ${data.conversations_transferred} conversations to user`);
    }
  } catch (err) {
    console.error('Failed to transfer conversations:', err);
  }
};

function App() {
  // Initialize user state from localStorage to prevent flash of unauthenticated state
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Error parsing stored user:', e);
      return null;
    }
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Sync user state with localStorage on changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
    setIsInitialized(true);
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Clear construction project cache to prevent stale data from other users/sessions
    localStorage.removeItem('construction_projects');
    localStorage.removeItem('current_project_id');
    
    // Transfer any anonymous conversations to the user
    const userId = userData?.id || userData?._id;
    if (userId) {
      transferConversations(userId);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('ai_estimator_conversation');
    // Clear construction project cache on logout
    localStorage.removeItem('construction_projects');
    localStorage.removeItem('current_project_id');
    localStorage.removeItem('watch_only_mode');
  };

  return (
    <LanguageProvider>
    <div className="App">
      <BrowserRouter>
        <AppHeader onLogout={handleLogout} />
        <div className="pt-16 pb-16 md:pb-0"> {/* pb-16 for mobile bottom nav */}
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/company-settings" element={<CompanySettingsPage user={user} />} />
          <Route path="/estimator" element={<EstimatorPage />} />
          <Route path="/ai-assistant" element={<AIAssistantPage user={user} />} />
          <Route path="/advice" element={<AdvicePage user={user} />} />
          <Route path="/guest-consultation" element={<GuestConsultationPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/construction" element={<ConstructionDashboard />} />
          <Route path="/construction/guide" element={<ConstructionInfographic />} />
          <Route path="/construction/budget" element={<BudgetToolsPage />} />
          <Route path="/construction/contractors" element={<SubcontractorPoolPage />} />
          <Route path="/construction/documents" element={<DocumentsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/free-estimator" element={<FreeEstimatorPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/admin/due-diligence" element={<AdminDueDiligence />} />
          <Route path="/house-plans" element={<HousePlansMarketplace />} />
          <Route path="/client-guide" element={<ClientGuidePage />} />
          <Route path="/construction-projects" element={<ConstructionProjectsPage />} />
          <Route path="/crm" element={<CRMPage />} />
          <Route path="/professional-estimator" element={<ProfessionalEstimatorPage />} />
          <Route path="/project-health" element={<ProjectHealthDashboard />} />
          
          {/* Marketplace Routes */}
          <Route path="/register-service" element={<ServiceProviderRegistration />} />
          <Route path="/submit-project" element={<ClientProjectSubmission />} />
          <Route path="/ceo-admin" element={<CEOAdminSection />} />
          
          {/* Admin Routes */}
          <Route path="/pricing-admin" element={<PricingDatabaseAdmin />} />
          <Route path="/marketplace-listings" element={<MarketplaceListings />} />
          <Route path="/ceo-digest" element={<CEODigestDashboard />} />
          <Route path="/custom-design" element={<CustomDesignRequest />} />
          <Route path="/plan-purchase-success" element={<PlanPurchaseSuccess />} />
          <Route path="/post-job" element={<PostJob />} />
          <Route path="/marketplace/job/:jobId" element={<MarketplaceJobDetails />} />
          <Route path="/contractor-dashboard" element={<ContractorDashboard />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/llm-discovery" element={<LLMDiscoveryPage />} />
          <Route path="/jobs" element={<JobsBoardPage />} />
          <Route path="/book-consultation" element={<BookConsultationPage />} />
          <Route path="/contact-sales" element={<ContactSalesPage />} />
          <Route path="/crown-pool" element={<CrownPoolPage />} />
          <Route path="/crown-pool/success" element={<CrownPoolSuccessPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsConditionsPage />} />
          <Route path="/team/approvals" element={<TeamApprovalsPage />} />
          <Route path="/ai-training" element={<AITrainingDashboard />} />
          <Route path="/vendor-registration" element={<VendorRegistrationPage />} />
          <Route path="/contract-templates" element={<ContractTemplatesPage />} />
          <Route path="/sign/:signingToken" element={<ContractSigningPage />} />
          {/* Feasibility is now integrated into the AI Assistant on the landing page */}
          <Route path="/feasibility" element={<Navigate to="/" replace />} />
          
          <Route path="/dashboard" element={
            !user ? <Navigate to="/login" /> :
            user.role === 'client' ? <ClientDashboard user={user} onLogout={handleLogout} /> :
            user.role === 'ceo' ? <CEODashboard user={user} onLogout={handleLogout} /> :
            user.role === 'subcontractor' ? <SubcontractorDashboard user={user} onLogout={handleLogout} /> :
            user.role === 'salesman' ? <SalesmanDashboard user={user} onLogout={handleLogout} /> :
            <Navigate to="/login" />
          } />
          </Routes>
          </Suspense>
          <SupportWidget />
        </div>
        <MobileBottomNav />
      </BrowserRouter>
      <Toaster />
      <AIConstructionAgent userContext={{ user: user?.name, role: user?.role }} />
    </div>
    </LanguageProvider>
  );
}

export default App;